<template>
  <div class="eguidance-vuetify">
    <div id="eguidance-app" :data-mode="mode" />
  </div>
  <div v-if="isPreviewEnabled && parseError">
    <p>Failed to parse technical configuration:</p>
    <code>
      {{ parseError }}
    </code>
  </div>
</template>

<script lang="ts" setup>
import { v } from 'h3-valibot';
import type { ITechnicalComponent } from '~/lib/ContentfulService';

interface Props {
  item: ITechnicalComponent;
  mode?: string;
}

const props = defineProps<Props>();
const {
  public: { isPreviewEnabled }
} = useRuntimeConfig();
const logger = useLogger();

const toolsOptionsSchema = v.object({
  mountFunction: v.string(),
  scripts: v.optional(
    v.array(
      v.object({
        type: v.optional(v.literal('module')),
        src: v.string(),
        crossorigin: v.optional(
          v.union([
            v.literal(''),
            v.literal('use-credentials'),
            v.literal('anonymous')
          ])
        ),
        tagPosition: v.optional(
          v.union([
            v.literal('head'),
            v.literal('bodyClose'),
            v.literal('bodyOpen')
          ])
        ),
        tagPriority: v.optional(v.number())
      })
    ),
    []
  ),
  links: v.optional(
    v.array(
      v.object({
        rel: v.optional(
          v.union([
            v.literal('canonical'),
            v.literal('dns-prefetch'),
            v.literal('manifest'),
            v.literal('modulepreload'),
            v.literal('nofollow'),
            v.literal('noopener'),
            v.literal('noreferrer'),
            v.literal('preconnect'),
            v.literal('prefetch'),
            v.literal('preload'),
            v.literal('prerender'),
            v.literal('search'),
            v.literal('stylesheet')
          ])
        ),
        as: v.optional(
          v.union([
            v.literal('object'),
            v.literal('audio'),
            v.literal('document'),
            v.literal('embed'),
            v.literal('fetch'),
            v.literal('font'),
            v.literal('image'),
            v.literal('script'),
            v.literal('style'),
            v.literal('track'),
            v.literal('video'),
            v.literal('worker')
          ])
        ),
        href: v.optional(v.string()),
        crossorigin: v.optional(
          v.union([
            v.literal(''),
            v.literal('use-credentials'),
            v.literal('anonymous')
          ])
        ),
        tagPosition: v.optional(
          v.union([
            v.literal('head'),
            v.literal('bodyClose'),
            v.literal('bodyOpen')
          ])
        ),
        tagPriority: v.optional(v.number())
      })
    ),
    []
  )
});

const parseResult = computed(() =>
  v.safeParse(toolsOptionsSchema, props.item.fields.configuration ?? {})
);
const configuration = computed(() => {
  if (parseResult.value.success) {
    return parseResult.value.output;
  }
});
const parseError = computed(() => {
  if (!parseResult.value.success) {
    return parseResult.value.output;
  }
});

const link = computed(() => configuration.value?.links ?? []);
const script = computed(() => configuration.value?.scripts ?? []);

useHead({
  link,
  script
});

const mountFunctionName = computed(() => configuration.value?.mountFunction);

function isFunction(input: unknown): input is Function {
  return typeof input === 'function';
}

function handleMountApp() {
  if (!mountFunctionName.value) {
    logger.warn('no mount function defined - aborting mount of tool.');

    return;
  }

  const mountFunction = window[mountFunctionName.value as any];
  if (!isFunction(mountFunction)) {
    logger.warn(
      `variable ${mountFunctionName.value} is not a function - aborting mount of tool.`
    );

    return;
  }

  try {
    mountFunction();
  } catch (e) {
    logger.error('ExternalTool', e);
  }
}

// we add a watcher if we switch between tools, this component
// might not be remounted.
watch(mountFunctionName, () => {
  handleMountApp();
});

onMounted(() => {
  // if tool scripts are already loaded we can directly call them
  handleMountApp();
  // add a listener to mount the tools. e.g. if we are navigating
  // from a non-tool-page to a tool page, the javascript is only loaded on the
  // tool page (emitting this event once loaded).
  // @see https://gcp.baslerweb.com/jira/browse/WEB2-2149
  window.addEventListener('basler-tools-ready', handleMountApp);
});

onUnmounted(() => {
  window.removeEventListener('basler-tools-ready', handleMountApp);
});
</script>
